import { lang } from '../../../commons';
import dayjs, { Dayjs } from 'dayjs';

/**
 * Centralized utility class for handling AM/PM translations across different locales
 */
export class AmPmTranslator {
  /**
   * Translation mappings for different locales
   */
  private static readonly LOCALE_TRANSLATIONS: Record<
    string,
    { am: string; pm: string }
  > = {
    el_GR: {
      am: 'π.μ.',
      pm: 'μ.μ.',
    },
  };

  /**
   * Get the AM/PM translation for a specific time and locale
   * @param date - Date object or dayjs moment
   * @param locale - Language locale (e.g., 'el_GR', 'en_US')
   * @param timezone - Optional timezone for date conversion
   * @returns Translated AM/PM string
   */
  public static getAmPmTranslation(
    date: Date | Dayjs | string,
    locale: lang,
    timezone?: string
  ): string {
    let moment: Dayjs;

    if (dayjs.isDayjs(date)) {
      moment = timezone ? date.tz(timezone) : date;
    } else {
      moment = timezone ? dayjs(date).tz(timezone) : dayjs(date);
    }

    const hour = moment.hour();
    const isAM = hour < 12;

    // Check if we have a custom translation for this locale
    const translation = this.LOCALE_TRANSLATIONS[locale];
    if (translation) {
      return isAM ? translation.am : translation.pm;
    }

    // Default behavior - use dayjs format for other locales
    return moment.format('a');
  }

  /**
   * Replace AM/PM in a formatted string with the correct translation
   * @param formattedString - String containing AM/PM (e.g., "2:30 PM")
   * @param date - Date to determine if it's AM or PM
   * @param locale - Language locale
   * @param timezone - Optional timezone
   * @returns String with translated AM/PM
   */
  public static replaceAmPmInString(
    formattedString: string,
    date: Date | Dayjs | string,
    locale: lang,
    timezone?: string
  ): string {
    if (!formattedString) return formattedString;

    // Only process if we have custom translations for this locale
    const translation = this.LOCALE_TRANSLATIONS[locale];
    if (!translation) return formattedString;

    const translatedAmPm = this.getAmPmTranslation(date, locale, timezone);

    // Replace AM/PM with translated version (case-insensitive, with optional spaces)
    return formattedString
      .replace(/\s?AM\s?/gi, ` ${translatedAmPm}`)
      .replace(/\s?PM\s?/gi, ` ${translatedAmPm}`);
  }

  /**
   * Format time with locale-specific AM/PM translation
   * @param date - Date to format
   * @param locale - Language locale
   * @param timezone - Optional timezone
   * @param format - Time format pattern (default: 'LT')
   * @returns Formatted time string with translated AM/PM
   */
  public static formatTimeWithAmPm(
    date: Date | Dayjs | string,
    locale: lang,
    timezone?: string,
    format: string = 'LT'
  ): string {
    let moment: Dayjs;

    if (dayjs.isDayjs(date)) {
      moment = timezone ? date.tz(timezone) : date;
    } else {
      moment = timezone ? dayjs(date).tz(timezone) : dayjs(date);
    }

    // Format with standard AM/PM first
    const formatted = moment.format(format);

    // Replace with translated AM/PM if needed
    return this.replaceAmPmInString(formatted, date, locale, timezone);
  }

  /**
   * Check if a locale has custom AM/PM translations
   * @param locale - Language locale to check
   * @returns True if locale has custom translations
   */
  public static hasCustomTranslation(locale: lang): boolean {
    return locale in this.LOCALE_TRANSLATIONS;
  }

  /**
   * Get all supported locales with custom AM/PM translations
   * @returns Array of supported locale codes
   */
  public static getSupportedLocales(): string[] {
    return Object.keys(this.LOCALE_TRANSLATIONS);
  }
}
